// The Reassign dialog and its toast with Undo (spec C6), for any page that offers Reassign: the lists,
// the Quick view and the Record. The page says what to do after a change (read the list again, or
// leave the Record when it is no longer yours).
import { useState } from 'react';
import type { Api } from '../auth/api';
import { Toast } from '../shell/Toast';
import { ReassignDialog } from './Reassign';
import type { Reassigned, Row } from './reassign-lines';

const canUndo = (r: Reassigned) =>
  !!r.undo || !!r.undoHandoffs?.length || !!r.undoTakeOvers?.length;

export function useReassign(
  api: Api,
  me: { id: string; name: string },
  onChanged: (r: Reassigned, rows: Row[]) => void,
  /** A toast handed over from the page before (the Record, after a VA moved it away). */
  initial: Reassigned | null = null,
) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [toast, setToast] = useState<(Reassigned & { id: number }) | null>(
    initial ? { ...initial, id: 0 } : null,
  );

  const undo = async (done: Reassigned) => {
    let back = true;
    if (done.undo) {
      const res = await api.reassign(done.undo, null);
      back = res.ok && res.body.moved.length === done.undo.length;
    }
    // Sent to an AE for approval: Undo cancels each request (and brings back what it ended).
    for (const id of done.undoHandoffs ?? []) {
      const res = await api.cancelHandoff(id);
      back = back && res.ok;
    }
    // Taken over: Undo puts each back with what it ended (S15 batch 7).
    for (const id of done.undoTakeOvers ?? []) {
      const res = await api.undoHandoffAnswer(id);
      back = back && res.ok;
    }
    const r: Reassigned = {
      text: back ? 'Undone. Back where it was.' : 'Could not undo all of it. Reassign it again.',
      undo: null,
    };
    setToast({ ...r, id: Date.now() });
    onChanged(r, []);
  };

  const element = (
    <>
      {rows && (
        <ReassignDialog
          api={api}
          rows={rows}
          me={me}
          onClose={() => setRows(null)}
          onDone={(r) => {
            const done = rows;
            setRows(null);
            setToast({ ...r, id: Date.now() });
            onChanged(r, done);
          }}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={canUndo(toast) ? { label: 'Undo', onClick: () => void undo(toast) } : undefined}
          shownMs={canUndo(toast) ? 8000 : undefined}
        />
      )}
    </>
  );

  return { open: (r: Row[]) => setRows(r), element };
}
